import { makeAutoObservable } from 'mobx';
import {
  SERVICE_PRICE_TYPE,
  Service,
  ServicesParams,
  getServices,
} from '../apiMethods/getServices';
import { ListWithCategories } from './paramsStore';

export type ServiceClickHandler = (params: {
  serviceId: number;
  subServiceId?: number;
  price?: number;
  name?: string;
  quantity?: number;
  annotation?: string;
}) => void;

export class ServiceStore {
  serviceList: ListWithCategories<Service>;

  constructor() {
    makeAutoObservable(this);

    this.serviceList = new ListWithCategories('Все категории');
  }

  private _selectedServices: {
    serviceId: number;
    subServiceId?: number;
    name: string;
    price: number;
    priceAnnotation?: string;
    quantity: number;
    comment?: string;
  }[] = [];

  async fetchServices(hotelId: number, bodyRequest: ServicesParams) {
    if (this.serviceList.pending) {
      return;
    }

    this.serviceList.setPending(true);

    const services = await getServices(hotelId, bodyRequest);
    services && this.serviceList.setItems(services);

    this.serviceList.setPending(false);
  }

  resetServices() {
    this._selectedServices = [];
  }

  getTotalPrice() {
    const servicesTotal = this._selectedServices.reduce((sum, x) => sum + x.price * x.quantity, 0);
    return servicesTotal;
  }

  get selectedServices() {
    return this._selectedServices;
  }

  getSelectedService(serviceId: number) {
    return this._selectedServices.find(x => x.serviceId === serviceId);
  }

  isServiceSelected(serviceId: number, subServiceId?: number) {
    const found = this._selectedServices.find(
      x => x.serviceId == serviceId && x.subServiceId == subServiceId,
    );
    return Boolean(found);
  }

  onServiceClick: ServiceClickHandler = ({
    serviceId,
    subServiceId,
    price,
    name,
    quantity,
    annotation,
  }) => {
    const service = this.serviceList.getItem(serviceId);

    if (service?.price_type === SERVICE_PRICE_TYPE.FIX_PRICE) {
      this.serviceHandler(service);
    }

    if (service?.price_type === SERVICE_PRICE_TYPE.SUB_SERVICE_PRICE) {
      if (!subServiceId || !price || !name) {
        throw new Error('missing required params');
      }
      this.subServiceHandler({
        serviceId,
        subServiceId,
        price,
        name,
      });
    }

    if (
      service?.price_type === SERVICE_PRICE_TYPE.PER_NIGTH &&
      quantity != undefined &&
      annotation
    ) {
      if (!annotation || quantity === undefined) {
        throw new Error('missing required params');
      }
      this.quantityServiceHandler(service, { serviceId, quantity, priceAnnotation: annotation });
    }
  };

  serviceHandler = (service: Service) => {
    this.toggleService({
      serviceId: service.id,
      name: service.name,
      price: service.price,
      quantity: 1,
    });
  };

  subServiceHandler = (params: {
    serviceId: number;
    subServiceId: number;
    name: string;
    price: number;
  }) => {
    const { serviceId, subServiceId, price, name } = params;

    this.toggleSubService({
      serviceId,
      subServiceId,
      price,
      name,
      quantity: 1,
    });
  };

  quantityServiceHandler = (
    service: Service,
    params: {
      serviceId: number;
      quantity: number;
      priceAnnotation: string;
    },
  ) => {
    const { serviceId, quantity, priceAnnotation } = params;

    this.toggleQuantityService({
      serviceId,
      quantity,
      name: service.name,
      price: service.price,
      priceAnnotation,
    });
  };

  toggleService(service: {
    serviceId: number;
    name: string;
    subServiceId?: number;
    price: number;
    quantity: number;
  }) {
    if (this._selectedServices.find(x => x.serviceId === service.serviceId)) {
      this._selectedServices = this._selectedServices.filter(
        x => x.serviceId !== service.serviceId,
      );
    } else {
      this._selectedServices.push(service);
    }
  }

  toggleQuantityService(service: {
    serviceId: number;
    quantity: number;
    name: string;
    price: number;
    priceAnnotation: string;
  }) {
    const exists = this._selectedServices.find(x => x.serviceId == service.serviceId);

    if (!exists) {
      this._selectedServices.push(service);
      return;
    }

    this._selectedServices = this._selectedServices.map(x => {
      if (x.serviceId === service.serviceId) {
        x.quantity = service.quantity;
      }
      return x;
    });

    this._selectedServices = this._selectedServices.filter(x =>
      x.serviceId === service.serviceId ? x.quantity > 0 : x,
    );
  }

  toggleSubService(service: {
    serviceId: number;
    subServiceId?: number;
    name: string;
    price: number;
    quantity: number;
  }) {
    if (
      this._selectedServices.find(
        x => x.serviceId === service.serviceId && x.subServiceId === service.subServiceId,
      )
    ) {
      this._selectedServices = this._selectedServices.filter(x => {
        return x.serviceId !== service.serviceId || x.subServiceId !== service.subServiceId;
      });
    } else {
      this._selectedServices.push(service);
    }
  }
}
